<script>
  import { onMount, untrack } from 'svelte';
  import { route, navigate } from '../../lib/stores/router.svelte.js';
  import {
    binderState,
    loadAndRenderBinder,
    resetBinderState,
    updateSelectionState,
    computePagination,
    goNextPage,
    goPrevPage,
    goToLocation,
    sortBinder,
    isMobile,
    getCollectionInfo,
  } from '../../lib/stores/binder.svelte.js';
  import { getCurrencySymbol, getCardDisplayName } from '../../lib/logic/utils.js';
  import {
    CARDS_PER_PAGE_FACE,
    COLOR_TAB_ORDER,
    COLOR_TAB_STYLES,
    COLOR_TAB_DISPLAY_NAMES,
    MD_BREAKPOINT,
  } from '../../lib/logic/config.js';
  import { getCardImageUrl } from '../../lib/services/card-utils.js';
  import {
    shouldPrefetch,
    loadMoreCards,
    loadColorCardsUpTo,
    hasPlaceholdersPastBuffer,
    initializeCollectionData,
  } from '../../lib/services/collection-api.js';
  import { addRecentBinder, normalizeToShortUrl } from '../../lib/services/recent-binders.js';
  import { shouldShowOverlapPill } from '../../lib/logic/want-overlap-logic.js';
  import { loadWantOverlap, overlapFor } from '../../lib/stores/want-overlap.svelte.js';
  import { loadWantHits } from '../../lib/stores/want-hits.svelte.js';
  import { zoomState, zoomIn, zoomOut } from '../../lib/stores/zoom.svelte.js';
  import { readDuration } from '../../lib/logic/motion.js';
  import {
    tradeState,
    enterTradeMode,
    exitTradeMode,
    setViewingSide,
    cacheBinder,
    getCachedBinder,
    isTradeMode,
  } from '../../lib/stores/trade.svelte.js';
  import {
    loadTrade,
    registerUserB,
    getUserSide,
    updateBinderInfo,
    subscribeToTrade,
    updateCardQuantityInTrade,
    updateCardStatus,
    removeCardFromTrade,
    suggestCard,
    addTokenToAllowedList,
    recoverSideToken,
  } from '../../lib/services/trade-service.js';
  import {
    getUserToken,
    storeSessionForTrade,
    getStoredSessionForTrade,
  } from '../../lib/stores/auth.svelte.js';
  import { detectTradeChanges } from '../../lib/logic/trade-notifications.js';
  import { processTradeChanges } from '../../lib/stores/notifications.svelte.js';
  import { getSelectionOverlayAction, createCardIdentifier } from '../../lib/logic/trade-logic.js';
  import {
    computeColorBoundaries,
    computeColorBoundariesFromOffsets,
    cardIndexToLocation,
    getActiveColorTab,
  } from '../../lib/logic/sort-logic.js';
  import BinderShell from './BinderShell.svelte';
  import BookPages from './BookPages.svelte';
  import ProposeTradeModal from '../trade/ProposeTradeModal.svelte';
  import CancelTradeModal from '../trade/CancelTradeModal.svelte';
  import ProposeTradeButton from './ProposeTradeButton.svelte';
  import { groupsState } from '../../lib/stores/groups.svelte.js';

  const currencySymbol = $derived(getCurrencySymbol(binderState.selectedCurrency));

  // Trade mode state
  let showProposeModal = $state(false);
  let showCancelModal = $state(false);

  const inTradeMode = $derived(isTradeMode());
  let loadedTradeId = null;

  // Want Overlap (spec 2026-09-21): the store is keyed by the binder URL it
  // was fetched for, so this is null until this binder's own overlap lands.
  const wantOverlap = $derived(overlapFor(binderState.binderUrl));
  const showOverlapPill = $derived(shouldShowOverlapPill(wantOverlap, inTradeMode));
  // The endpoint returns the wanter's uid only; name and avatar come from the
  // groups store (every shared Group's members, loaded at sign-in), the same
  // source as the "Wanted by" modal below. A wanter missing from the
  // snapshots gets null (the "?" avatar), never a raw uid. Null keeps the
  // plain button.
  const pillWanter = $derived(
    showOverlapPill && wantOverlap?.wanter
      ? {
          uid: wantOverlap.wanter.uid,
          displayName: groupsState.memberUidToName[wantOverlap.wanter.uid] ?? null,
          photoUrl: groupsState.memberUidToPhoto[wantOverlap.wanter.uid] ?? null,
        }
      : null
  );

  // Color tabs (visible only in color sort mode)
  // Prefer backend-provided offsets (instant, covers all cards) with fallback to scanning loaded cards
  const colorBoundaries = $derived.by(() => {
    if (binderState.currentSortMode !== 'color') return [];
    if (binderState.colorOffsets) {
      return computeColorBoundariesFromOffsets(binderState.colorOffsets);
    }
    return computeColorBoundaries(binderState.allCardsData);
  });
  const activeColorKey = $derived.by(() => {
    if (colorBoundaries.length === 0) return null;
    return getActiveColorTab(colorBoundaries, binderState.currentLocation, isMobile());
  });

  // Map paperIndex → tab info for pages that start a color section.
  // Tab goes on the right-side page (paper whose front is visible at that location),
  // so it always protrudes from the right edge and never needs to flip.
  const paperTabMap = $derived.by(() => {
    if (colorBoundaries.length === 0) return {};
    const map = {};
    const mobile = isMobile();
    const totalTabs = colorBoundaries.length;
    colorBoundaries.forEach((boundary, i) => {
      // Use location as paperIndex — on desktop, location L = paper L's front (right side)
      const pi = cardIndexToLocation(boundary.firstCardIndex, mobile);
      // Only store first tab per paper (in case two sections start on same view)
      if (!(pi in map)) {
        map[pi] = {
          key: boundary.key,
          label: boundary.label,
          tabIndex: i,
          totalTabs,
        };
      }
    });
    return map;
  });

  function getViewCardUrls(location) {
    const cards = binderState.allCardsData;
    const mobile = isMobile();
    const urls = [];
    let start, end;
    if (mobile) {
      start = location * CARDS_PER_PAGE_FACE;
      end = start + CARDS_PER_PAGE_FACE;
    } else {
      if (location === 0) return urls;
      start = CARDS_PER_PAGE_FACE + (location - 1) * CARDS_PER_PAGE_FACE * 2;
      end = start + CARDS_PER_PAGE_FACE;
    }
    let bStart = -1,
      bEnd = -1;
    if (!mobile && location > 0) {
      const pp = location - 1;
      if (pp === 0) {
        bStart = 0;
        bEnd = CARDS_PER_PAGE_FACE;
      } else {
        bStart = CARDS_PER_PAGE_FACE + (pp - 1) * CARDS_PER_PAGE_FACE * 2 + CARDS_PER_PAGE_FACE;
        bEnd = bStart + CARDS_PER_PAGE_FACE;
      }
    }
    for (let i = start; i < end && i < cards.length; i++) {
      const url = getCardImageUrl(cards[i]?.cardData);
      if (url) urls.push(url);
    }
    for (let i = bStart; i < bEnd && i >= 0 && i < cards.length; i++) {
      const url = getCardImageUrl(cards[i]?.cardData);
      if (url) urls.push(url);
    }
    return urls;
  }

  function preloadImages(urls) {
    return Promise.all(
      urls.map(
        (url) =>
          new Promise((resolve) => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = resolve;
            img.src = url;
          })
      )
    );
  }

  // Single-flip tab jump: pin old pages in buffer, jump, let CSS animate the flip
  let extraBufferedPages = $state([]);
  let zBoostedPages = $state([]); // which pinned pages get z-index 2500
  let pinnedBackOverrides = $state([]); // [{ paperIndex, start, end }] — override back faces of pinned pages
  let isTabJumping = $state(false);

  function handleColorTabJump(key) {
    if (isTabJumping) return;
    const boundary = colorBoundaries.find((b) => b.key === key);
    if (!boundary) return;
    const target = cardIndexToLocation(boundary.firstCardIndex, isMobile());
    const current = binderState.currentLocation;
    if (target === current) return;

    const forward = target > current;

    // Ensure cards at the target are loaded before flipping
    // Need enough cards to fill the visible view (target page + 9 cards)
    const targetCardIndex = boundary.firstCardIndex + CARDS_PER_PAGE_FACE;

    async function prepareAndFlip() {
      // Load cards up to the target if this is a collection with unloaded pages
      if (binderState.isCollection && binderState.collectionData) {
        await loadColorCardsUpTo(
          targetCardIndex,
          getCollectionInfo(),
          binderState.collectionData,
          binderState.binderSyncStatus,
          binderState.cardCache
        );
      }

      // Preload images for the target view
      const urls = getViewCardUrls(target);
      if (urls.length > 0) {
        await Promise.race([preloadImages(urls), new Promise((r) => setTimeout(r, 2000))]);
      }

      doFlip();
    }

    prepareAndFlip();

    function doFlip() {
      isTabJumping = true;

      if (forward) {
        // Pin old right page (animates flip) and old left page (stays visible during flip).
        // Override the right page's back face to show the NEW left page cards,
        // so when it flips over, the back matches the destination's left page.
        const mobile = isMobile();
        extraBufferedPages = mobile ? [current] : [current, current - 1];
        zBoostedPages = mobile ? [current] : [current, current - 1]; // both old pages stay on top
        const newLeftPaper = target - 1;
        if (!isMobile() && newLeftPaper >= 0) {
          let bStart, bEnd;
          if (newLeftPaper === 0) {
            bStart = 0;
            bEnd = CARDS_PER_PAGE_FACE;
          } else {
            bStart =
              CARDS_PER_PAGE_FACE +
              (newLeftPaper - 1) * CARDS_PER_PAGE_FACE * 2 +
              CARDS_PER_PAGE_FACE;
            bEnd = bStart + CARDS_PER_PAGE_FACE;
          }
          pinnedBackOverrides = [{ paperIndex: current, start: bStart, end: bEnd }];
        }
        goToLocation(target);
      } else {
        // Backward: target page un-flips from left to reveal new view.
        // Pin un-flipping page + old right + old left (mirrors forward).
        // Override target page's back face to show old left page cards,
        // so before un-flip the back matches what was on the left.
        const mobile = isMobile();
        extraBufferedPages = mobile ? [target] : [target, current, current - 1];
        zBoostedPages = mobile ? [] : [current, current - 1];
        if (!mobile) {
          // Override target page's back: show old left content (paper current-1 back)
          // This is what's visible on the left during the first half of the un-flip
          const oldLeftPaper = current - 1;
          if (oldLeftPaper >= 0) {
            let bStart, bEnd;
            if (oldLeftPaper === 0) {
              bStart = 0;
              bEnd = CARDS_PER_PAGE_FACE;
            } else {
              bStart =
                CARDS_PER_PAGE_FACE +
                (oldLeftPaper - 1) * CARDS_PER_PAGE_FACE * 2 +
                CARDS_PER_PAGE_FACE;
              bEnd = bStart + CARDS_PER_PAGE_FACE;
            }
            pinnedBackOverrides = [{ paperIndex: target, start: bStart, end: bEnd }];
          }
        }
        // Double rAF: first frame renders target page flipped in DOM,
        // second frame paints it, THEN we un-flip so CSS transition has a starting point
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            unflippingIndex = target;
            goToLocation(target);
          });
        });
      }

      // Clean up after flip animation completes
      if (!forward && !isMobile()) {
        // Backward: swap old left page's back to new left content just before removing,
        // so the removal is seamless (old left matches new left underneath)
        const oldLeftPaper = current - 1;
        const newLeftPaper = target - 1;
        setTimeout(() => {
          if (oldLeftPaper >= 0 && newLeftPaper >= 0) {
            let nStart, nEnd;
            if (newLeftPaper === 0) {
              nStart = 0;
              nEnd = CARDS_PER_PAGE_FACE;
            } else {
              nStart =
                CARDS_PER_PAGE_FACE +
                (newLeftPaper - 1) * CARDS_PER_PAGE_FACE * 2 +
                CARDS_PER_PAGE_FACE;
              nEnd = nStart + CARDS_PER_PAGE_FACE;
            }
            pinnedBackOverrides = [{ paperIndex: oldLeftPaper, start: nStart, end: nEnd }];
          }
          requestAnimationFrame(() => {
            extraBufferedPages = [];
            zBoostedPages = [];
            pinnedBackOverrides = [];
            unflippingIndex = -1;
            isTabJumping = false;
          });
        }, 700);
      } else if (!isMobile()) {
        // Forward desktop: swap old left page's back to new left content before removing
        const fwdOldLeftPaper = current - 1;
        const fwdNewLeftPaper = target - 1;
        setTimeout(() => {
          if (fwdOldLeftPaper >= 0 && fwdNewLeftPaper >= 0) {
            let nStart, nEnd;
            if (fwdNewLeftPaper === 0) {
              nStart = 0;
              nEnd = CARDS_PER_PAGE_FACE;
            } else {
              nStart =
                CARDS_PER_PAGE_FACE +
                (fwdNewLeftPaper - 1) * CARDS_PER_PAGE_FACE * 2 +
                CARDS_PER_PAGE_FACE;
              nEnd = nStart + CARDS_PER_PAGE_FACE;
            }
            pinnedBackOverrides = [{ paperIndex: fwdOldLeftPaper, start: nStart, end: nEnd }];
          }
          requestAnimationFrame(() => {
            extraBufferedPages = [];
            zBoostedPages = [];
            pinnedBackOverrides = [];
            unflippingIndex = -1;
            isTabJumping = false;
          });
        }, 700);
      } else {
        setTimeout(() => {
          extraBufferedPages = [];
          zBoostedPages = [];
          pinnedBackOverrides = [];
          unflippingIndex = -1;
          isTabJumping = false;
        }, 700);
      }
    }
  }

  // Pagination display
  const isDesktop = $derived(typeof window !== 'undefined' && window.innerWidth >= MD_BREAKPOINT);
  const minLocation = $derived(isDesktop && binderState.numPapers > 0 ? 1 : 0);
  const showPagination = $derived(binderState.maxLocation > 0);

  const prevDisabled = $derived(binderState.currentLocation <= minLocation);
  const nextDisabled = $derived.by(() => {
    if (binderState.currentLocation >= binderState.maxLocation) return true;
    // For collections, disable next if we'd hit placeholders past the buffer
    if (binderState.isCollection && binderState.collectionData) {
      const bufferSize = isDesktop ? 3 : 2;
      const cardsPerPage = isDesktop ? CARDS_PER_PAGE_FACE * 2 : CARDS_PER_PAGE_FACE;
      return hasPlaceholdersPastBuffer(
        binderState.currentLocation,
        cardsPerPage,
        bufferSize,
        true,
        binderState.currentSortMode,
        binderState.collectionData
      );
    }
    return false;
  });

  const pageInfoText = $derived.by(() => {
    if (binderState.numPapers === 0) return 'View 0 of 0';
    if (isDesktop && binderState.numPapers > 0) {
      const total = binderState.numPapers - 1 > 0 ? binderState.numPapers - 1 : 1;
      return `View ${binderState.currentLocation} of ${total}`;
    }
    return `View ${binderState.currentLocation + 1} of ${binderState.maxLocation + 1}`;
  });

  // Page turning guard with cooldown
  let isPageTurning = false;
  const FLIP_COOLDOWN = 300;

  // Track which paper index is currently un-flipping backward (needs high z-index)
  let unflippingIndex = $state(-1);
  let unflipTimeout = null;

  function handleNextPage() {
    if (isPageTurning || isTabJumping) return;
    isPageTurning = true;
    goNextPage();
    scheduleCollectionPrefetch();
    setTimeout(() => {
      isPageTurning = false;
    }, FLIP_COOLDOWN);
  }

  function handlePrevPage() {
    if (isPageTurning || isTabJumping) return;
    isPageTurning = true;

    // The page that will un-flip is at currentLocation - 1
    // Temporarily raise its z-index so it appears on top during the backward animation
    const pageToUnflip = binderState.currentLocation - 1;
    unflippingIndex = pageToUnflip;
    if (unflipTimeout) clearTimeout(unflipTimeout);
    unflipTimeout = setTimeout(() => {
      unflippingIndex = -1;
      unflipTimeout = null;
    }, readDuration('--dur-page-flip'));

    goPrevPage();
    setTimeout(() => {
      isPageTurning = false;
    }, FLIP_COOLDOWN);
  }

  // Keyboard active state for pagination buttons
  let prevKeyActive = $state(false);
  let nextKeyActive = $state(false);

  let mobileColorPickerOpen = $state(false);

  function handleSort(mode) {
    if (mode === 'color' && binderState.currentSortMode === 'color' && isMobile()) {
      // Already in color mode on mobile — toggle picker
      mobileColorPickerOpen = !mobileColorPickerOpen;
      return;
    }
    if (mode === 'color' && isMobile()) {
      // Switching to color mode on mobile — show picker immediately
      sortBinder(mode);
      scheduleCollectionPrefetch();
      mobileColorPickerOpen = true;
      return;
    }
    mobileColorPickerOpen = false;
    sortBinder(mode);
    scheduleCollectionPrefetch();
  }

  function handleMobileColorPick(key) {
    mobileColorPickerOpen = false;
    handleColorTabJump(key);
  }

  function handleSelect(originalIndex, newCount) {
    if (inTradeMode) {
      handleTradeSelect(originalIndex, newCount);
      return;
    }
    updateSelectionState(originalIndex, newCount);
  }

  /**
   * Build a full card identifier with all fields needed by trade-service Firestore writes.
   * createCardIdentifier() returns only matching fields; this adds display/price data.
   */
  function buildFullCardIdentifier(cardInfo) {
    const { cardData, isFoil, price, priceCurrency, availableCount, lang } = cardInfo;
    return {
      scryfallId: cardData.id,
      name: getCardDisplayName(cardData, cardInfo.originalLineRef),
      set: cardData.set,
      setName: cardData.set_name,
      collectorNumber: cardData.collector_number,
      isFoil: isFoil,
      lang: lang || cardData.lang || 'en',
      availableQuantity: availableCount || 1,
      priceUsd: priceCurrency === 'usd' ? price : 0,
      priceEur: priceCurrency === 'eur' ? price : 0,
      rarity: cardData.rarity || 'common',
    };
  }

  async function handleTradeSelect(originalIndex, currentSelectedCount) {
    const cardInfo = binderState.allCardsData[originalIndex];
    if (!cardInfo || cardInfo.status !== 'found' || !cardInfo.cardData) return;

    const tradeId = tradeState.currentTradeId;
    const viewingSide = tradeState.viewingSide;
    const userSide = tradeState.userSide;

    try {
      if (currentSelectedCount > 0) {
        // Card is in trade — cycle status or remove
        const action = getSelectionOverlayAction(cardInfo, tradeState);
        if (action.type === 'cycle') {
          await updateCardStatus(tradeId, viewingSide, action.cardIdentifier, action.nextStatus);
        } else if (action.type === 'remove' || action.type === 'deselect') {
          // 'remove': cycling past considering on other's binder
          // 'deselect': unsuggest on own binder (suggested cards don't toggle)
          // Optimistic update: clear selection and local trade state immediately
          cardInfo.selectedCount = 0;
          const cardId = createCardIdentifier(cardInfo);
          // Remove from local trade snapshot so tradeStatus goes null instantly (no icon flash)
          const sideKey = `side${viewingSide}`;
          const sideCards = tradeState.currentTrade?.[sideKey]?.cards;
          if (sideCards) {
            const idx = sideCards.findIndex(
              (c) =>
                c.scryfallId === cardId.scryfallId &&
                c.set === cardId.set &&
                c.collectorNumber?.toLowerCase() === cardId.collectorNumber?.toLowerCase() &&
                c.isFoil === cardId.isFoil
            );
            if (idx >= 0) sideCards.splice(idx, 1);
          }
          await removeCardFromTrade(tradeId, viewingSide, cardId);
        }
      } else {
        // Card not in trade — add it (use full identifier with name/prices)
        const cardIdentifier = buildFullCardIdentifier(cardInfo);
        if (userSide !== viewingSide) {
          // Viewing other's binder — add as trade card
          await updateCardQuantityInTrade(tradeId, viewingSide, cardIdentifier, 1);
        } else {
          // Viewing own binder — suggest card
          await suggestCard(tradeId, viewingSide, cardIdentifier);
        }
      }
    } catch (err) {
      console.error('[Trade] Error handling trade card click:', err);
    }
  }

  async function handleTradeQuantity(originalIndex, newQuantity) {
    const cardInfo = binderState.allCardsData[originalIndex];
    if (!cardInfo || cardInfo.status !== 'found' || !cardInfo.cardData) return;

    const tradeId = tradeState.currentTradeId;
    const viewingSide = tradeState.viewingSide;
    const cardIdentifier = buildFullCardIdentifier(cardInfo);

    try {
      await updateCardQuantityInTrade(tradeId, viewingSide, cardIdentifier, newQuantity);
    } catch (err) {
      console.error('[Trade] Error updating trade quantity:', err);
    }
  }

  function handleZoom(originalIndex, cardInfo, slotElement) {
    if (!slotElement) return;
    zoomIn(cardInfo, slotElement);
  }

  function handleZoomOverlayClick() {
    zoomOut();
  }

  // Keyboard navigation with visual feedback
  function handleKeydown(e) {
    // Escape closes zoom
    if (e.key === 'Escape' && zoomState.active) {
      e.preventDefault();
      zoomOut();
      return;
    }
    // Don't navigate pages while zoomed
    if (zoomState.active) return;

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      window.dispatchEvent(
        new CustomEvent('drawer-toggle', {
          detail: { direction: e.key === 'ArrowUp' ? 'open' : 'close' },
        })
      );
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      nextKeyActive = true;
      handleNextPage();
      setTimeout(() => {
        nextKeyActive = false;
      }, 150);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prevKeyActive = true;
      handlePrevPage();
      setTimeout(() => {
        prevKeyActive = false;
      }, 150);
    }
  }

  // Collection prefetch scheduling
  let collectionPrefetchTimeout = null;

  function scheduleCollectionPrefetch() {
    if (!binderState.isCollection || !binderState.collectionData) return;

    if (collectionPrefetchTimeout) {
      clearTimeout(collectionPrefetchTimeout);
    }

    collectionPrefetchTimeout = setTimeout(() => {
      const mobile = isMobile();
      const bufferSize = mobile ? 2 : 3;
      const cardsPerPage = mobile ? CARDS_PER_PAGE_FACE : CARDS_PER_PAGE_FACE * 2;

      if (
        shouldPrefetch(
          binderState.currentLocation,
          cardsPerPage,
          bufferSize,
          true,
          binderState.currentSortMode,
          binderState.collectionData,
          binderState.binderSyncStatus
        )
      ) {
        loadMoreCards(
          getCollectionInfo(),
          binderState.collectionData,
          binderState.currentSortMode,
          binderState.binderSyncStatus,
          binderState.cardCache
        )
          .then(({ loaded, syncLanded }) => {
            // A sync probe saw a newer sync land: a COLD binder's items now
            // exist, so the star's want hits can find them.
            if (syncLanded) loadWantHits(binderState.binderUrl);
            if (loaded) {
              // Chain prefetch if more data available
              scheduleCollectionPrefetch();
            } else {
              // Retry after delay if skipped (already loading)
              setTimeout(() => scheduleCollectionPrefetch(), 500);
            }
          })
          .catch((err) => {
            console.error('[BinderView] Prefetch error:', err);
            setTimeout(() => scheduleCollectionPrefetch(), 2000);
          });
      }

      collectionPrefetchTimeout = null;
    }, 100);
  }

  // Track the currently loaded URL/trade to detect changes
  let loadedBinderUrl = null;

  // Load binder or trade reactively when route params change
  $effect(() => {
    const tradeId = route.params.tradeId;
    const tradeMode = route.params.tradeMode;
    const binderUrl = route.params.binderUrl;

    if (tradeMode && tradeId && tradeId !== loadedTradeId) {
      loadedTradeId = tradeId;
      loadedBinderUrl = null;
      untrack(() => loadTradeView(tradeId));
    } else if (!tradeMode && binderUrl && binderUrl !== loadedBinderUrl) {
      loadedBinderUrl = binderUrl;
      loadedTradeId = null;
      untrack(() => {
        exitTradeMode();
        if (binderState.binderFullyLoaded) {
          resetBinderState();
        }
        // The star's want hits, in parallel with the (public) page.
        loadWantHits(binderUrl);
        loadAndRenderBinder(binderUrl).then((result) => {
          if (result) {
            addRecentBinder({
              name: result.name,
              owner: result.owner,
              url: window.location.href,
              originalUrl: result.originalUrl,
            });
            scheduleCollectionPrefetch();
            loadWantOverlap(binderUrl);
            if (!isMobile() && binderState.numPapers > 1) {
              binderState.currentLocation = 1;
            }
          }
        });
      });
    }
  });

  // ============================================================
  // Trade mode functions
  // ============================================================

  async function loadTradeView(tradeId) {
    try {
      console.log('[Trade] Loading trade view for ID:', tradeId);
      resetBinderState();
      binderState.isLoading = true;
      binderState.loadingText = 'Loading trade...';

      // Wait for auth
      const userToken = await getUserToken();

      // Load trade doc
      const trade = await loadTrade(tradeId);

      // Check if cancelled
      if (trade.cancelledBy || trade.status === 'cancelled') {
        binderState.isLoading = false;
        binderState.error = {
          title: 'Trade Cancelled',
          message: 'This trade has been cancelled.',
        };
        return;
      }

      // Register user B if needed
      if (!trade.sideB.userToken && trade.sideA.userToken !== userToken) {
        await registerUserB(tradeId);
        trade.sideB.userToken = userToken;
        storeSessionForTrade(tradeId, userToken);
      }

      // Determine user side
      let userSide = await getUserSide(trade);

      // iOS auth recovery: if user side not found, check stored session
      if (!userSide) {
        const storedToken = getStoredSessionForTrade(tradeId);
        console.log('[Trade] No user side found, checking stored session:', storedToken);

        if (storedToken) {
          // Check if the stored token matches either side
          const matchedSide =
            storedToken === trade.sideA.userToken
              ? 'A'
              : storedToken === trade.sideB.userToken
                ? 'B'
                : null;

          if (matchedSide) {
            console.log(
              '[Trade] Found matching stored session, recovering auth for side',
              matchedSide
            );
            try {
              await addTokenToAllowedList(tradeId);
              await recoverSideToken(tradeId, matchedSide);
              const updatedTrade = await loadTrade(tradeId);
              Object.assign(trade, updatedTrade);
              userSide = await getUserSide(trade);
              console.log('[Trade] iOS recovery successful! User side:', userSide);
            } catch (recoveryError) {
              console.warn('[Trade] iOS auth recovery failed:', recoveryError);
            }
          }
        }
      }

      if (!userSide) {
        binderState.isLoading = false;
        binderState.error = {
          title: 'Trade Not Found',
          message: "This trade doesn't exist or you don't have permission to view it.",
        };
        return;
      }

      // Store session for recovery
      storeSessionForTrade(tradeId, userToken);

      // Enter trade mode
      enterTradeMode(tradeId, userSide);
      tradeState.currentTrade = trade;

      // Load the OTHER user's binder (start viewing their side)
      const viewingSide = tradeState.viewingSide;
      const binderUrl = viewingSide === 'A' ? trade.sideA.binderUrl : trade.sideB.binderUrl;

      const result = await loadAndRenderBinder(binderUrl);

      if (!result) {
        binderState.error = {
          title: 'Trade Error',
          message: 'Failed to load binder for this trade.',
        };
        return;
      }

      // Add binder to recents
      addRecentBinder({
        name: result.name,
        owner: result.owner,
        url: binderUrl,
        originalUrl: result.originalUrl,
      });

      // Backfill binder info for old trades
      updateBinderInfo(tradeId, viewingSide, result).catch((err) => {
        console.warn('[Trade] Failed to backfill binder info:', err);
      });

      // Cache this binder for instant side-swapping
      cacheBinder(viewingSide, snapshotCurrentBinder());

      // Apply trade selections from Firestore data
      applyTradeSelections(trade);

      // Subscribe to Firestore updates AFTER initialization
      tradeState.tradeUnsubscribe = subscribeToTrade(tradeId, handleTradeUpdate);

      // On desktop, flip to page 1 (skip cover)
      if (!isMobile() && binderState.numPapers > 1) {
        binderState.currentLocation = 1;
      }

      // Update document title
      document.title = result.owner ? `Trade with ${result.owner}` : 'Trade';

      // Preload user's own binder in background
      preloadOtherBinder(trade);
    } catch (error) {
      console.error('[Trade] Error loading trade:', error);
      binderState.isLoading = false;

      if (
        error.message?.includes('Trade not found') ||
        error.message?.includes('Missing or insufficient permissions') ||
        error.code === 'permission-denied'
      ) {
        binderState.error = {
          title: 'Trade Not Found',
          message: "This trade doesn't exist or you don't have permission to view it.",
        };
      } else {
        binderState.error = {
          title: 'Trade Error',
          message: 'Failed to load trade. Please check your connection and try again.',
        };
      }
    }
  }

  function handleTradeUpdate(trade) {
    if (!trade) return;

    // Check if cancelled remotely
    if (trade.cancelledBy || trade.status === 'cancelled') {
      if (tradeState.tradeUnsubscribe) {
        tradeState.tradeUnsubscribe();
        tradeState.tradeUnsubscribe = null;
      }
      exitTradeMode();
      binderState.binderFullyLoaded = false;
      binderState.error = {
        title: 'Trade Cancelled',
        message: 'This trade has been cancelled by the other party.',
      };
      return;
    }

    // Detect changes (skip self-notifications)
    if (tradeState.previousTradeSnapshot && !tradeState.pendingLocalChange) {
      try {
        const userToken =
          tradeState.userSide === 'A'
            ? tradeState.currentTrade?.sideA?.userToken
            : tradeState.currentTrade?.sideB?.userToken;
        const changes = detectTradeChanges(tradeState.previousTradeSnapshot, trade, userToken);
        if (changes.length > 0) {
          processTradeChanges(changes);
        }
      } catch (e) {
        console.warn('[Trade] Error detecting changes:', e);
      }
    }

    tradeState.pendingLocalChange = false;
    tradeState.previousTradeSnapshot = structuredClone(trade);
    tradeState.currentTrade = trade;

    // Exchange-only fast path
    if (tradeState.pendingExchangeUpdate) {
      tradeState.pendingExchangeUpdate = false;
      // TODO: Update exchange states only (Phase 6d)
      return;
    }

    applyTradeSelections(trade);
  }

  function applyTradeSelections(trade) {
    const side = tradeState.viewingSide;
    const sideData = side === 'A' ? trade.sideA : trade.sideB;

    tradeState.applyingRemoteUpdate = true;
    binderState.selectedCards = [];

    binderState.allCardsData.forEach((cardInfo) => {
      if (cardInfo.status !== 'found' || !cardInfo.cardData) return;

      const tradeCard = sideData.cards.find(
        (c) =>
          c.scryfallId === cardInfo.cardData.id &&
          c.set === cardInfo.cardData.set &&
          c.collectorNumber?.toLowerCase() === cardInfo.cardData.collector_number?.toLowerCase() &&
          c.isFoil === cardInfo.isFoil
      );

      if (tradeCard && tradeCard.quantity > 0) {
        cardInfo.selectedCount = Math.min(tradeCard.quantity, cardInfo.availableCount);

        binderState.selectedCards.push({
          id: cardInfo.cardData.id,
          name: getCardDisplayName(cardInfo.cardData, cardInfo.originalLineRef),
          set: cardInfo.cardData.set,
          set_name: cardInfo.cardData.set_name,
          collector_number: cardInfo.cardData.collector_number?.toLowerCase(),
          isFoil: cardInfo.isFoil,
          price: cardInfo.price,
          priceCurrency: cardInfo.priceCurrency,
          selectedCount: cardInfo.selectedCount,
          availableCount: cardInfo.availableCount,
          lang: cardInfo.lang,
          rarity: cardInfo.cardData.rarity,
        });
      } else {
        cardInfo.selectedCount = 0;
      }
    });

    tradeState.applyingRemoteUpdate = false;
  }

  async function swapTradeSides() {
    if (!isTradeMode() || !tradeState.currentTrade) return;

    const currentSide = tradeState.viewingSide;

    // Cache current binder data
    cacheBinder(currentSide, snapshotCurrentBinder());

    const newSide = currentSide === 'A' ? 'B' : 'A';
    const trade = tradeState.currentTrade;
    const binderUrl = newSide === 'A' ? trade.sideA.binderUrl : trade.sideB.binderUrl;
    const normalizedUrl = normalizeToShortUrl(binderUrl);

    // Check in-memory cache
    const cached = getCachedBinder(newSide);

    if (cached && cached.url === normalizedUrl) {
      // Restore binder data FIRST, then flip the tab — all in one sync batch
      restoreBinderFromSnapshot(cached);
      setViewingSide(newSide);
      applyTradeSelections(trade);
      scheduleCollectionPrefetch();
    } else {
      // Load fresh — flip tab immediately since we need to show loading state
      setViewingSide(newSide);
      resetBinderState();
      const result = await loadAndRenderBinder(binderUrl);
      if (result) {
        cacheBinder(newSide, snapshotCurrentBinder());
        if (!isMobile() && binderState.numPapers > 1 && binderState.currentLocation === 0) {
          binderState.currentLocation = 1;
        }
      }
      applyTradeSelections(trade);
    }

    // Update document title
    const viewedSideData = newSide === 'A' ? trade.sideA : trade.sideB;
    document.title = viewedSideData?.ownerName ? `Trade with ${viewedSideData.ownerName}` : 'Trade';
  }

  function snapshotCurrentBinder() {
    const snapshot = {
      url: normalizeToShortUrl(binderState.binderUrl),
      binderColor: binderState.binderColor,
      currentPage: binderState.currentLocation,
      sortMode: binderState.currentSortMode,
      isCollection: binderState.isCollection,
      binderName: binderState.binderName,
      binderOwner: binderState.binderOwner,
      selectedCurrency: binderState.selectedCurrency,
      binderSyncStatus: binderState.binderSyncStatus,
    };

    if (binderState.isCollection && binderState.collectionData) {
      snapshot.collectionType = binderState.collectionType;
      snapshot.collectionSource = binderState.collectionSource;
      snapshot.collectionSourceId = binderState.collectionSourceId;
      snapshot.totalCards = binderState.collectionData.totalCards;
      snapshot.initialPriceCards = binderState.collectionData.byPrice
        .filter((c) => c.status !== 'placeholder')
        .map((c) => c.cardData);
      snapshot.initialColorCards = binderState.collectionData.byColor
        .filter((c) => c.status !== 'placeholder')
        .map((c) => c.cardData);
      snapshot.priceHasMore = binderState.collectionData.priceLoadState?.hasMore ?? false;
      snapshot.colorHasMore = binderState.collectionData.colorLoadState?.hasMore ?? false;
    } else {
      snapshot.allCardsData = binderState.allCardsData.map((c) => ({ ...c }));
    }

    return snapshot;
  }

  function restoreBinderFromSnapshot(snapshot) {
    binderState.binderUrl = snapshot.url;
    binderState.binderColor = snapshot.binderColor;
    binderState.currentLocation = snapshot.currentPage;
    binderState.currentSortMode = snapshot.sortMode;
    binderState.binderName = snapshot.binderName || '';
    binderState.binderOwner = snapshot.binderOwner || '';
    binderState.selectedCurrency = snapshot.selectedCurrency;
    binderState.binderSyncStatus = snapshot.binderSyncStatus || 'complete';

    if (snapshot.isCollection && snapshot.totalCards) {
      binderState.isCollection = true;
      binderState.collectionType = snapshot.collectionType;
      binderState.collectionSource = snapshot.collectionSource;
      binderState.collectionSourceId = snapshot.collectionSourceId;

      // Re-initialize collection data from cached metadata
      binderState.cardCache = new Map();
      binderState.collectionData = initializeCollectionData({
        totalCards: snapshot.totalCards,
        initialPriceCards: snapshot.initialPriceCards,
        initialColorCards: snapshot.initialColorCards,
        priceHasMore: snapshot.priceHasMore,
        colorHasMore: snapshot.colorHasMore,
        syncedAt: null,
        cardCache: binderState.cardCache,
        selectedCurrency: binderState.selectedCurrency,
        source: snapshot.collectionSource,
        collectionType: snapshot.collectionType,
        sourceId: snapshot.collectionSourceId,
      });

      binderState.allCardsData =
        snapshot.sortMode === 'price'
          ? binderState.collectionData.byPrice
          : binderState.collectionData.byColor;
    } else {
      binderState.isCollection = false;
      binderState.collectionData = null;
      binderState.allCardsData = snapshot.allCardsData.map((c) => ({ ...c }));
    }

    computePagination(binderState.allCardsData.length);
    binderState.binderFullyLoaded = true;
    binderState.isLoading = false;
    binderState.error = null;
    binderState.selectedCards = [];
  }

  async function preloadOtherBinder(trade) {
    const otherSide = tradeState.viewingSide === 'A' ? 'B' : 'A';
    const otherUrl = otherSide === 'A' ? trade.sideA.binderUrl : trade.sideB.binderUrl;
    const normalizedUrl = normalizeToShortUrl(otherUrl);

    const cached = getCachedBinder(otherSide);
    if (cached?.url === normalizedUrl) return;

    try {
      // Prime IndexedDB cache for fast loading on side swap
      const { loadBinder: loadBinderData } = await import('../../lib/services/binder-api.js');
      await loadBinderData(otherUrl, {
        currency: binderState.selectedCurrency,
      });
      console.log('[Trade Preload] Primed IndexedDB cache for', otherSide);
    } catch (err) {
      console.warn('[Trade Preload] Failed to preload:', err);
    }
  }

  function handleTradeCreated(tradeId) {
    showProposeModal = false;
    navigate('binder', { tradeId, tradeMode: true });
  }

  function handleTradeCancelled() {
    showCancelModal = false;
    exitTradeMode();
    navigate('home');
  }

  // Cleanup on unmount
  onMount(() => {
    return () => {
      if (collectionPrefetchTimeout) {
        clearTimeout(collectionPrefetchTimeout);
      }
      // Clean up trade subscription
      if (tradeState.tradeUnsubscribe) {
        tradeState.tradeUnsubscribe();
        tradeState.tradeUnsubscribe = null;
      }
    };
  });
</script>

<svelte:window onkeydown={handleKeydown} />

<div class="binder-view-area">
  {#if binderState.isLoading}
    <!-- Loading state -->
    <div class="binder-loading">
      <div class="binder-loading-spinner"></div>
      <p class="binder-loading-text">{binderState.loadingText}</p>
    </div>
  {:else if binderState.error}
    <!-- Error state -->
    <div class="binder-error">
      <h2 class="binder-error-title">{binderState.error.title}</h2>
      <p class="binder-error-message">{binderState.error.message}</p>
    </div>
  {:else if binderState.binderFullyLoaded}
    <!-- Binder loaded -->

    <!-- Sort Controls + Trade Header (same row) -->
    {#if binderState.binderSyncStatus !== 'syncing'}
      <div class="sort-controls">
        <div class="sort-group-left"></div>
        <div class="sort-group-center">
          <div class="sort-tabs">
            <button
              class="sort-tab"
              class:active={binderState.currentSortMode === 'price'}
              onclick={() => handleSort('price')}
              title="Sort by Price (High to Low)"
            >
              <span class="price-sort-icon">{currencySymbol}</span>
            </button>
            <button
              class="sort-tab"
              class:active={binderState.currentSortMode === 'color'}
              onclick={() => handleSort('color')}
              title="Sort by Color"
            >
              <!-- Color wheel SVG -->
              <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                <path d="M 50 0 A 50 50 0 0 1 97.55 34.55 L 50 50 Z" fill="#f8fafc" />
                <path d="M 97.55 34.55 A 50 50 0 0 1 79.39 90.45 L 50 50 Z" fill="#3b82f6" />
                <path d="M 79.39 90.45 A 50 50 0 0 1 20.61 90.45 L 50 50 Z" fill="#1f2937" />
                <path d="M 20.61 90.45 A 50 50 0 0 1 2.45 34.55 L 50 50 Z" fill="#ef4444" />
                <path d="M 2.45 34.55 A 50 50 0 0 1 50 0 L 50 50 Z" fill="#22c55e" />
                <line x1="50" y1="50" x2="50" y2="0" stroke="#d1d5db" stroke-width="1" />
                <line x1="50" y1="50" x2="97.55" y2="34.55" stroke="#d1d5db" stroke-width="1" />
                <line x1="50" y1="50" x2="79.39" y2="90.45" stroke="#d1d5db" stroke-width="1" />
                <line x1="50" y1="50" x2="20.61" y2="90.45" stroke="#d1d5db" stroke-width="1" />
                <line x1="50" y1="50" x2="2.45" y2="34.55" stroke="#d1d5db" stroke-width="1" />
              </svg>
            </button>
          </div>
          {#if mobileColorPickerOpen}
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <div
              class="mobile-color-backdrop"
              onclick={() => (mobileColorPickerOpen = false)}
            ></div>
            <div class="mobile-color-picker">
              {#each COLOR_TAB_ORDER as key (key)}
                {@const style = COLOR_TAB_STYLES[key]}
                {@const hasBoundary = colorBoundaries.some((b) => b.key === key)}
                <button
                  class="color-pick"
                  class:active={key === activeColorKey}
                  class:disabled={!hasBoundary}
                  disabled={!hasBoundary}
                  onclick={() => handleMobileColorPick(key)}
                  title={COLOR_TAB_DISPLAY_NAMES[key] || key}
                >
                  {#if style?.svg}
                    <img
                      class="pick-icon"
                      src={style.svg}
                      alt={COLOR_TAB_DISPLAY_NAMES[key] || key}
                    />
                  {:else}
                    <span class="pick-label">{style?.label || key}</span>
                  {/if}
                </button>
              {/each}
            </div>
          {/if}
        </div>
        <div class="sort-group-right">
          {#if inTradeMode}
            <!-- Trade mode: binder tabs -->
            <div class="binder-tabs">
              <button
                class="binder-tab"
                class:active={tradeState.viewingSide !== tradeState.userSide}
                onclick={() => {
                  if (tradeState.viewingSide === tradeState.userSide) swapTradeSides();
                }}
              >
                <span class="binder-tab-full">Their Binder</span>
                <span class="binder-tab-short">Theirs</span>
              </button>
              <button
                class="binder-tab"
                class:active={tradeState.viewingSide === tradeState.userSide}
                onclick={() => {
                  if (tradeState.viewingSide !== tradeState.userSide) swapTradeSides();
                }}
              >
                <span class="binder-tab-full">Your Binder</span>
                <span class="binder-tab-short">Yours</span>
              </button>
            </div>
          {:else}
            <!-- Normal mode: the button, in its Want Overlap state when there is something to say -->
            <ProposeTradeButton
              wanter={pillWanter}
              cardCount={wantOverlap?.cardCount ?? 0}
              onclick={() => (showProposeModal = true)}
            />
          {/if}
        </div>
      </div>
    {/if}

    <BinderShell
      binderColor={binderState.binderColor}
      onSwipeLeft={handleNextPage}
      onSwipeRight={handlePrevPage}
    >
      <BookPages
        currentLocation={binderState.currentLocation}
        numPapers={binderState.numPapers}
        allCardsData={binderState.allCardsData}
        {currencySymbol}
        {unflippingIndex}
        {paperTabMap}
        {activeColorKey}
        {extraBufferedPages}
        {zBoostedPages}
        {pinnedBackOverrides}
        onColorTabJump={handleColorTabJump}
        onSelect={handleSelect}
        onZoom={handleZoom}
        onTradeQuantity={handleTradeQuantity}
      />
    </BinderShell>

    <!-- Zoom background overlay -->
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="zoom-overlay"
      class:active={zoomState.active && !zoomState.closing}
      onclick={handleZoomOverlayClick}
    ></div>

    <!-- Pagination Controls -->
    {#if showPagination}
      <div class="pagination-controls">
        <button
          class="pagination-btn"
          class:keyboard-active={prevKeyActive}
          disabled={prevDisabled}
          onclick={handlePrevPage}
          aria-label="Previous page"
          title="Previous Page"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <span class="page-info">{pageInfoText}</span>
        <button
          class="pagination-btn"
          class:keyboard-active={nextKeyActive}
          disabled={nextDisabled}
          onclick={handleNextPage}
          aria-label="Next page"
          title="Next Page"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      </div>
    {/if}
  {/if}
</div>

<!-- Trade modals (fixed position, rendered outside binder-view-area) -->
<ProposeTradeModal
  isOpen={showProposeModal}
  currentBinderUrl={binderState.binderUrl}
  overlapBinders={wantOverlap?.binders ?? []}
  onClose={() => (showProposeModal = false)}
  onTradeCreated={handleTradeCreated}
/>

<CancelTradeModal
  isOpen={showCancelModal}
  tradeId={tradeState.currentTradeId}
  onClose={() => (showCancelModal = false)}
  onCancelled={handleTradeCancelled}
/>

<style>
  .binder-view-area {
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
    height: auto;
    /* CSS Variables for binder styling */
    --binder-corner-radius: 1.6rem;
    --binder-shell-padding-y: 0.7rem;
    --binder-shell-padding-x: 1.1rem;
    --binder-stitch-inset: 0.25rem;
    --page-corner-radius: 0.3rem;
    --card-corner-radius: 4.5%;
    --page-bg-color: #272b30;
    --inner-page-padding: 1.6rem;
    --page-outer-padding: 0.6rem;
    --card-grid-gap: 0.75rem;
    /* z-index stacking */
    --z-binder-stitch: 1;
    --z-binder-shell: 2;
    --z-book-pages: 10;
    --z-page-unflipping: 2500;
    --z-card-stitch: 4;
    --z-card-foil: 5;
    --z-card-language: 6;
    --z-card-selection: 7;
    --z-card-overlay: 8;
    --z-card-quantity: 9;
  }

  /* Loading state */
  .binder-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 60dvh;
    gap: 1rem;
  }

  .binder-loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(0, 0, 0, 0.1);
    border-top-color: #6b7280;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .binder-loading-text {
    color: #6b7280;
    font-size: 0.875rem;
  }

  /* Error state */
  .binder-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 60dvh;
    text-align: center;
  }

  .binder-error-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .binder-error-message {
    color: #6b7280;
    font-size: 0.875rem;
    max-width: 400px;
  }

  /* Sort controls — 3-column layout: left spacer | center sort tabs | right trade/propose */
  .sort-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 0 0 auto;
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
    width: 100%;
  }

  .sort-group-left,
  .sort-group-right {
    flex: 1;
  }

  .sort-group-left {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .sort-group-center {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex: 1;
    position: relative;
  }

  .sort-group-right {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
    padding-right: 0.5rem;
  }

  .sort-tabs {
    display: flex;
    gap: 0.25rem;
    background: #e5e7eb;
    padding: 0.3rem;
    border-radius: 9999px;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
    cursor: pointer;
  }

  .sort-tab {
    background: transparent;
    border: none;
    border-radius: 50%;
    padding: 0;
    margin: 0;
    cursor: pointer;
    transition:
      background var(--dur-base),
      box-shadow var(--dur-base);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
  }

  .sort-tab:hover:not(.active) {
    background: rgba(255, 255, 255, 0.6);
  }

  .sort-tab.active {
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  }

  .sort-tab svg {
    width: 20px;
    height: 20px;
  }

  .price-sort-icon {
    font-size: 1.1rem;
    font-weight: 600;
    color: #4b5563;
    line-height: 1;
  }

  .sort-tab.active .price-sort-icon {
    color: #059669;
  }

  /* Mobile color picker — pops up below sort toggle */
  .mobile-color-backdrop {
    display: none;
  }

  .mobile-color-picker {
    display: none;
  }

  @media (max-width: 767px) {
    .mobile-color-backdrop {
      display: block;
      position: fixed;
      inset: 0;
      z-index: 5;
    }

    .mobile-color-picker {
      display: flex;
      justify-content: center;
      gap: 0.35rem;
      background: #e5e7eb;
      padding: 0.3rem 0.5rem;
      border-radius: 9999px;
      box-shadow:
        inset 0 1px 2px rgba(0, 0, 0, 0.1),
        0 2px 8px rgba(0, 0, 0, 0.15);
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      z-index: 6;
      animation: pickerPop 0.2s ease-out;
      transform-origin: top center;
    }

    @keyframes pickerPop {
      from {
        transform: translateX(-50%) scaleY(0);
        opacity: 0;
      }
      to {
        transform: translateX(-50%) scaleY(1);
        opacity: 1;
      }
    }

    .color-pick {
      width: 32px;
      height: 32px;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      transition:
        background var(--dur-fast),
        transform var(--dur-fast);
      padding: 0;
    }

    .color-pick:active {
      transform: scale(0.9);
    }

    .color-pick.active {
      background: white;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    }

    .color-pick.disabled {
      opacity: 0.3;
      cursor: default;
    }

    .pick-icon {
      width: 24px;
      height: 24px;
      pointer-events: none;
    }

    .pick-label {
      font-size: 0.7rem;
      font-weight: 700;
    }
  }

  /* Binder tabs (trade mode) */
  .binder-tabs {
    display: flex;
    gap: 0.25rem;
    background: #e5e7eb;
    padding: 0.3rem;
    border-radius: 9999px;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  .binder-tab {
    background: transparent;
    color: #4b5563;
    border: none;
    border-radius: 9999px;
    padding: 0 1.25rem;
    height: 38px;
    font-weight: 500;
    font-size: 0.95rem;
    cursor: pointer;
    transition:
      background var(--dur-base),
      color var(--dur-base),
      box-shadow var(--dur-base);
    display: flex;
    align-items: center;
  }

  .binder-tab:hover:not(.active) {
    background: rgba(255, 255, 255, 0.6);
    color: #1f2937;
  }

  .binder-tab.active {
    background: white;
    color: #059669;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  }

  /* Desktop: show full text, hide short */
  .binder-tab-short {
    display: none;
  }

  .binder-tab-full {
    display: inline;
  }

  @media (max-width: 767px) {
    .sort-tabs {
      padding: 0.25rem;
    }
    .sort-tab {
      width: 34px;
      height: 34px;
    }
    .sort-tab svg {
      width: 18px;
      height: 18px;
    }
    .price-sort-icon {
      font-size: 1rem;
    }
    .binder-tabs {
      padding: 0.25rem;
    }
    .binder-tab {
      padding: 0 0.85rem;
      height: 34px;
      font-size: 0.85rem;
    }
    /* Mobile: show short text, hide full */
    .binder-tab-short {
      display: inline;
    }
    .binder-tab-full {
      display: none;
    }
  }

  /* Pagination controls */
  .pagination-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    margin: 0.5rem 0;
    padding: 0 0.75rem;
    box-sizing: border-box;
    position: relative;
    z-index: 1;
  }

  .pagination-btn {
    background-color: #f9fafb;
    color: #374151;
    padding: 0;
    border-radius: 0.25rem;
    border: 1px solid #d1d5db;
    box-shadow: 0 2px 0px 0px #d1d5db;
    cursor: pointer;
    transition:
      background-color var(--dur-fast) ease-in-out,
      border-color var(--dur-fast) ease-in-out,
      box-shadow var(--dur-fast) ease-in-out,
      transform var(--dur-instant) ease-in-out;
    margin: 0 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    touch-action: manipulation;
  }

  .pagination-btn svg {
    width: 20px;
    height: 20px;
    stroke-width: 2.5;
  }

  .pagination-btn:hover:not(:disabled) {
    background-color: #f3f4f6;
    border-color: #adb5bd;
    box-shadow: 0 2px 0px 0px #adb5bd;
  }

  .pagination-btn:active:not(:disabled) {
    background-color: #e5e7eb;
    transform: translateY(1px);
    box-shadow: 0 1px 0px 0px #adb5bd;
  }

  .pagination-btn:disabled {
    background-color: #f3f4f6;
    border-color: #e5e7eb;
    cursor: not-allowed;
    opacity: 0.6;
    box-shadow: 0 2px 0px 0px #e5e7eb;
    transform: translateY(0);
  }

  .pagination-btn:disabled svg {
    stroke: #9ca3af;
  }

  .pagination-btn.keyboard-active {
    background-color: #e5e7eb;
    transform: translateY(1px);
    box-shadow: 0 1px 0px 0px #adb5bd;
  }

  .page-info {
    font-size: 0.875rem;
    color: #374151;
    font-weight: 500;
    margin: 0 0.5rem;
  }

  /* Desktop layout */
  @media (min-width: 768px) {
    .binder-view-area {
      --binder-controls-height: 7rem;
      --binder-padding: 2rem;
      --selected-cards-bar-height: 55px;
      --available-height: calc(
        100dvh - var(--binder-controls-height) - var(--binder-padding) -
          var(--selected-cards-bar-height)
      );
      height: calc(100dvh - var(--selected-cards-bar-height) - env(safe-area-inset-bottom, 0px));
      width: 100%;
      max-width: min(100rem, calc(var(--available-height) * (10 / 7)));
      padding: 1rem;
      margin: 0 auto;
      display: flex;
      flex-direction: column;
      justify-content: center;
      min-height: 0;
      overflow: clip;
      overflow-x: visible;
      gap: 1rem;
      box-sizing: border-box;
    }
  }

  @media (max-width: 767px) {
    .binder-view-area {
      margin-left: 0;
      margin-right: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: calc(100dvh - 55px - env(safe-area-inset-bottom, 0px));
      overflow: clip;
      overflow-x: visible;
    }
  }

  /* Ensure binder shell shrinks when space is tight (iOS Safari) */
  .binder-view-area :global(.binder-container) {
    flex: 0 1 auto;
    min-height: 0;
  }

  /* Zoom overlay — fades in/out to match the zoom-travel animation's duration
     and easing. Reads the same --dur-zoom/--ease-smooth tokens CardSlot's
     zoom-travel WAAPI animation reads (motion.js/zoom.svelte.js) instead of a
     hardcoded literal, so the backdrop can never drift out of sync with the
     card the next time --dur-zoom changes (it silently did after Task 10
     retimed --dur-zoom to 350ms while this stayed hardcoded at 0.5s). */
  .zoom-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.85);
    z-index: 1000;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity var(--dur-zoom) var(--ease-smooth),
      visibility 0s linear var(--dur-zoom);
    pointer-events: none;
  }
  .zoom-overlay.active {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transition:
      opacity var(--dur-zoom) var(--ease-smooth),
      visibility 0s linear 0s;
  }
</style>
